import React, { useState, useEffect } from "react";
import { Card, CardBody, CardTitle, Container, Row } from "reactstrap";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datetime";
import qs from "query-string";
import api from "service/api";
import Loading from "components/Loading";
import { BarChart } from "@mui/x-charts/BarChart";
import "react-datetime/css/react-datetime.css";

const ClientSummary = () => {
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [summaryData, setSummaryData] = useState();
  const [clientBudget, setClientBudget] = useState(0);
  const [loading, setLoading] = useState(true);

  const [allLLMEventsPerPeriod, setAllLLMEventsPerPeriod] = useState([]);
  const [allTranscriptionEventsPerPeriod, setAllTranscriptionEventsPerPeriod] =
    useState([]);
  const [allStorageEventsPerPeriod, setAllStorageEventsPerPeriod] = useState(
    [],
  );

  const handleDateChange = async (date) => {
    let normalizedDate = date;
    if (date && typeof date === "object" && typeof date.toDate === "function") {
      normalizedDate = date.toDate();
    } else if (!(date instanceof Date)) {
      normalizedDate = new Date(date);
    }
    if (isNaN(normalizedDate.getTime())) {
      return;
    }
    setLoading(true);
    setSelectedDate(normalizedDate);

    setSummaryData(null);
    setAllLLMEventsPerPeriod([]);
    setAllTranscriptionEventsPerPeriod([]);
    setAllStorageEventsPerPeriod([]);
    setClientBudget(0);

    let errors = [];
    const [summaryError, llmError, transcriptionError, storageError] =
      await Promise.all([
        fetchSummaryData(normalizedDate, true),
        fetchAllLLMEventsPerPeriod(true, normalizedDate),
        fetchAllTranscriptionEventsPerPeriod(true, normalizedDate),
        fetchAllStorageEventsPerPeriod(true, normalizedDate),
      ]);
    if (summaryError) errors.push(summaryError);

    const noDataTypes = [];
    if (llmError === "info") {
      noDataTypes.push("Análises generativas");
    } else if (llmError) errors.push(llmError);

    if (transcriptionError === "info") {
      noDataTypes.push("Transcrições");
    } else if (transcriptionError) errors.push(transcriptionError);

    if (storageError === "info") {
      noDataTypes.push("Armazenamento");
    } else if (storageError) errors.push(storageError);

    setLoading(false);
    if (errors.length > 0) {
      toast.error(errors.join("\n"));
    } else if (noDataTypes.length > 0) {
      const message = `Nenhum registro encontrado para: ${noDataTypes.join(", ")} neste período.`;
      toast.info(message);
    }
  };

  const getMonthDateRange = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const startDate = `${year}-${String(month).padStart(2, "0")}-01`;
    const endDate = `${year}-${String(month).padStart(2, "0")}-${String(new Date(year, month, 0).getDate()).padStart(2, "0")}`;
    return { startDate, endDate };
  };

  const getParamsId = () => {
    const queryParams = qs.parse(window.location.search);
    const id = queryParams.id;
    return id ? id : undefined;
  };

  useEffect(() => {
    handleDateChange(selectedDate);
    // eslint-disable-next-line
  }, []);

  const fetchSummaryData = async (date, silent = false) => {
    setLoading(true);
    setSummaryData(null);
    try {
      const response = await api.get("/client/summary", {
        params: {
          clientId: getParamsId(),
          date: date.toISOString(),
        },
      });
      const segments = response.data.segments || [];
      const analysisArr = response?.data?.analysisSummary?.analysis || [];
      const summaryBySegment = segments.map((item) => {
        const analysis = analysisArr.find(
          (analysis) => analysis?.segment_id === item?.id,
        );

        return {
          name: item?.title,
          isDeleted: item?.deletedAt ? true : false,
          monthlyExpense: analysis?.total_price || 0,
          totalAttendances: analysis?.total_attendances || 0,
          totalAnalyses: analysis?.total_analyses || 0,
        };
      });
      const clientData = {
        name: response?.data?.name,
        assistants: response?.data?.assistants,
        segments: summaryBySegment.filter(
          (segment) => segment.totalAnalyses > 0,
        ),
        totalMonthlyExpense: analysisArr.reduce(
          (acc, segment) => acc + (segment.total_price || 0),
          0,
        ),
        totalEvents: analysisArr.reduce(
          (acc, segment) => acc + (segment.total_attendances || 0),
          0,
        ),
        totalAnalysis: analysisArr.reduce(
          (acc, segment) => acc + (segment.total_analyses || 0),
          0,
        ),
        budgetLimit: response?.data?.budgetLimit || [],
      };
      setSummaryData(clientData);
      setLoading(false);
      return null;
    } catch (error) {
      console.error(error);
      setLoading(false);
      if (silent) return "Erro ao buscar resumo do cliente.";
      return null;
    }
  };

  const fetchAllLLMEventsPerPeriod = async (
    silent = false,
    date = selectedDate,
  ) => {
    setLoading(true);
    setAllLLMEventsPerPeriod([]);
    const { startDate, endDate } = getMonthDateRange(date);
    try {
      const response = await api.get("/client/budget/summary", {
        params: {
          client: getParamsId(),
          start: startDate,
          end: endDate,
          type: "LLM",
          unit: "ALL",
        },
      });
      setAllLLMEventsPerPeriod(
        Array.isArray(response.data.usage) ? response.data.usage : [],
      );
      setClientBudget(response.data.client_budget || 0);
      setLoading(false);
      return null;
    } catch (error) {
      console.error(error);
      setLoading(false);
      if (silent && error?.response?.status === 404) return "info";
      if (silent) return "Erro ao buscar eventos LLM.";
      return null;
    }
  };

  const fetchAllTranscriptionEventsPerPeriod = async (
    silent = false,
    date = selectedDate,
  ) => {
    setLoading(true);
    setAllTranscriptionEventsPerPeriod([]);
    const { startDate, endDate } = getMonthDateRange(date);

    const transcriptionUsageEvents = [];
    let hasError = false;
    let errorResponse = null;

    try {
      const oldTranscriptionUsage = await api.get("/client/budget/summary", {
        params: {
          client: getParamsId(),
          start: startDate,
          end: endDate,
          type: "TRANSCRIPTION",
          unit: "AUDIO_DURATION",
        },
      });

      if (oldTranscriptionUsage.data.usage) {
        transcriptionUsageEvents.push(...oldTranscriptionUsage.data.usage);
      }
    } catch (error) {
      console.error("Error fetching old transcription usage:", error);
      hasError = true;
      errorResponse = error;
    }

    try {
      const newTranscriptionUsage = await api.get("/client/budget/summary", {
        params: {
          client: getParamsId(),
          start: startDate,
          end: endDate,
          type: "TRANSCRIPTION",
          unit: "PROCESSED_DURATION",
        },
      });

      if (newTranscriptionUsage.data.usage) {
        transcriptionUsageEvents.push(...newTranscriptionUsage.data.usage);
      }
    } catch (error) {
      console.error("Error fetching new transcription usage:", error);
      hasError = true;
      errorResponse = error;
    }

    const consolidatedEvents = {};
    transcriptionUsageEvents.forEach((event) => {
      const date = event.date;
      if (consolidatedEvents[date]) {
        consolidatedEvents[date].total += event.total || 0;
        consolidatedEvents[date].total_requests += event.total_requests || 0;
        consolidatedEvents[date].total_quantity += event.total_quantity || 0;
      } else {
        consolidatedEvents[date] = { ...event };
      }
    });

    const finalEvents = Object.values(consolidatedEvents);
    setAllTranscriptionEventsPerPeriod(finalEvents);
    setLoading(false);

    if (hasError && finalEvents.length === 0) {
      if (silent && errorResponse?.response?.status === 404) return "info";
      if (silent) return "Erro ao buscar eventos de transcrição.";
    }

    return null;
  };

  const fetchAllStorageEventsPerPeriod = async (
    silent = false,
    date = selectedDate,
  ) => {
    setLoading(true);
    setAllStorageEventsPerPeriod([]);
    const { startDate, endDate } = getMonthDateRange(date);
    try {
      const response = await api.get("/client/budget/summary", {
        params: {
          client: getParamsId(),
          start: startDate,
          end: endDate,
          type: "STORAGE",
          unit: "MB",
        },
      });
      setAllStorageEventsPerPeriod(
        Array.isArray(response.data.usage) ? response.data.usage : [],
      );
      setLoading(false);
      return null;
    } catch (error) {
      console.error(error);
      setLoading(false);
      if (silent && error?.response?.status === 404) return "info";
      if (silent) return "Erro ao buscar eventos de armazenamento.";
      return null;
    }
  };

  function TotalSpendBarChart({ llmData, transcriptionData, storageData }) {
    let normalizedDate = selectedDate;
    if (!(normalizedDate instanceof Date)) {
      normalizedDate = new Date(normalizedDate);
    }
    llmData = Array.isArray(llmData) ? llmData : [];
    transcriptionData = Array.isArray(transcriptionData)
      ? transcriptionData
      : [];
    storageData = Array.isArray(storageData) ? storageData : [];

    const year = normalizedDate.getFullYear();
    const month = normalizedDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const allDates = Array.from({ length: daysInMonth }, (_, i) => {
      const d = new Date(year, month, i + 1);
      return String(d.getDate()).padStart(2, "0");
    });

    const getDay = (dateStr) => {
      if (!dateStr) return "";
      if (dateStr.length === 2) return dateStr;
      return dateStr.split("-")[2];
    };

    const chartData = allDates.map((day) => {
      const llm = llmData.find((e) => getDay(e.date) === day)?.total || 0;
      const trans =
        transcriptionData.find((e) => getDay(e.date) === day)?.total || 0;
      const storage =
        storageData.find((e) => getDay(e.date) === day)?.total || 0;
      return llm + trans + storage;
    });

    return (
      <BarChart
        height={300}
        series={[
          {
            data: chartData,
            color: "#0d47a1",
            barWidth: 24,
          },
        ]}
        xAxis={[
          {
            data: allDates,
            scaleType: "band",
            barGapRatio: 0.2,
            tickLabelStyle: {
              fontWeight: "bold",
            },
          },
        ]}
        yAxis={[
          {
            axisLine: {
              stroke: "#e0e0e0",
              strokeWidth: 2,
            },
          },
        ]}
        grid={{ horizontal: false, vertical: false }}
        leftAxis={null}
        margin={{ right: 20, top: 20, bottom: 40 }}
      />
    );
  }

  function EventsBarChart({ title, data, color }) {
    let normalizedDate = selectedDate;
    if (!(normalizedDate instanceof Date)) {
      normalizedDate = new Date(normalizedDate);
    }
    data = Array.isArray(data) ? data : [];
    const year = normalizedDate.getFullYear();
    const month = normalizedDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const allDates = Array.from({ length: daysInMonth }, (_, i) => {
      const d = new Date(year, month, i + 1);
      return String(d.getDate()).padStart(2, "0");
    });
    const getDay = (dateStr) => {
      if (!dateStr) return "";
      if (dateStr.length === 2) return dateStr;
      return dateStr.split("-")[2];
    };
    const totals = allDates.map((day) => {
      return data.find((e) => getDay(e.date) === day)?.total || 0;
    });
    let barColor = color;
    if (title === "LLM Events") barColor = "#0d47a1";
    if (title === "Transcription Events") barColor = "#1b5e20";
    if (title === "Storage Events") barColor = "#f57c00";
    return (
      <BarChart
        height={250}
        series={[
          {
            data: totals,
            color: barColor,
            barWidth: 24,
          },
        ]}
        xAxis={[
          {
            data: allDates,
            scaleType: "band",
            barGapRatio: 0.2,
            tickLabelStyle: {
              fontWeight: "bold",
            },
          },
        ]}
        yAxis={[
          {
            axisLine: {
              stroke: "#e0e0e0",
              strokeWidth: 2,
            },
          },
        ]}
        grid={{ horizontal: false, vertical: false }}
        leftAxis={null}
        margin={{ right: 20, top: 20, bottom: 40 }}
      />
    );
  }

  return (
    <>
      <Container className="mt-2" fluid>
        <h1>Resumo do cliente - {summaryData?.name}</h1>

        {loading ? <Loading /> : null}

        <Row className="mb-3 pr-3" style={{ gap: "14px" }}>
          <Card id="total-spend-card" style={{ width: "calc(80% - 0.5rem)" }}>
            <CardBody>
              <CardTitle>
                <b>Total utilizado</b>
              </CardTitle>
              <div
                style={{
                  fontSize: "0.8rem",
                  color: "#666",
                  marginBottom: "0.5rem",
                }}
              >
                Total: $
                {(
                  allLLMEventsPerPeriod.reduce(
                    (sum, item) => sum + (item.total || 0),
                    0,
                  ) +
                  allTranscriptionEventsPerPeriod.reduce(
                    (sum, item) => sum + (item.total || 0),
                    0,
                  ) +
                  allStorageEventsPerPeriod.reduce(
                    (sum, item) => sum + (item.total || 0),
                    0,
                  )
                ).toFixed(2)}{" "}
                | Requisições:{" "}
                {allLLMEventsPerPeriod.reduce(
                  (sum, item) => sum + (item.total_requests || 0),
                  0,
                ) +
                  allTranscriptionEventsPerPeriod.reduce(
                    (sum, item) => sum + (item.total_requests || 0),
                    0,
                  ) +
                  allStorageEventsPerPeriod.reduce(
                    (sum, item) => sum + (item.total_requests || 0),
                    0,
                  )}{" "}
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                <div style={{ flex: 1 }} />
                <TotalSpendBarChart
                  llmData={allLLMEventsPerPeriod}
                  transcriptionData={allTranscriptionEventsPerPeriod}
                  storageData={allStorageEventsPerPeriod}
                />
              </div>
            </CardBody>
          </Card>

          <Card
            id="client-summary-card"
            style={{ width: "calc(20% - 0.5rem)" }}
          >
            <CardBody>
              <CardTitle style={{ fontSize: "1.1rem" }}>
                <b>
                  {selectedDate
                    .toLocaleDateString("pt-BR", {
                      month: "long",
                      year: "numeric",
                    })
                    .replace(/^\w/, (c) => c.toUpperCase())}
                </b>
              </CardTitle>
              <div
                style={{
                  marginBottom: "0",
                }}
              >
                <DatePicker
                  inputProps={{
                    placeholder: "Selecione o mês...",
                    style: {
                      fontSize: "0.85rem",
                      padding: "4px 8px",
                      height: "32px",
                      width: "100%",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                    },
                  }}
                  dateFormat="MM/YYYY"
                  timeFormat={false}
                  value={selectedDate}
                  onChange={handleDateChange}
                  viewMode="months"
                  closeOnSelect={true}
                  closeOnTab={true}
                />
              </div>
              <div style={{ marginTop: "1rem" }}>
                {(() => {
                  const totalSpent = (
                    allLLMEventsPerPeriod.reduce(
                      (sum, item) => sum + (item.total || 0),
                      0,
                    ) +
                    allTranscriptionEventsPerPeriod.reduce(
                      (sum, item) => sum + (item.total || 0),
                      0,
                    ) +
                    allStorageEventsPerPeriod.reduce(
                      (sum, item) => sum + (item.total || 0),
                      0,
                    )
                  ).toFixed(2);

                  const progressPercentage =
                    clientBudget > 0
                      ? Math.min(
                          (parseFloat(totalSpent) / clientBudget) * 100,
                          100,
                        )
                      : 0;

                  return (
                    <>
                      <div
                        style={{
                          fontSize: "0.9rem",
                          marginBottom: "0.5rem",
                          fontWeight: "bold",
                          textAlign: "center",
                        }}
                      >
                        ${totalSpent} / ${clientBudget}
                      </div>
                      <div
                        style={{
                          width: "100%",
                          height: "30px",
                          backgroundColor: "#e0e0e0",
                          borderRadius: "2px",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: `${progressPercentage}%`,
                            height: "100%",
                            backgroundColor:
                              progressPercentage > 90
                                ? "#f44336"
                                : progressPercentage > 70
                                  ? "#ff9800"
                                  : "#4caf50",
                            transition: "width 0.3s ease",
                          }}
                        />
                      </div>
                      <div
                        style={{
                          fontSize: "0.7rem",
                          color: "#666",
                          textAlign: "center",
                          marginTop: "0.2rem",
                        }}
                      >
                        {progressPercentage.toFixed(1)}% do orçamento utilizado
                      </div>
                    </>
                  );
                })()}
              </div>
            </CardBody>
            <CardBody style={{ padding: "0 22px" }}>
              <p>Quantidade de Assistentes: {summaryData?.assistants}</p>
              <p>
                Quantidade de Segmentos Ativos:{" "}
                {(summaryData?.segments || []).length}
              </p>
              <p>Eventos Analisados: {summaryData?.totalEvents}</p>
              <p>Prompts Analisados: {summaryData?.totalAnalysis}</p>

              <hr style={{ margin: "1rem 0", borderColor: "#e0e0e0" }} />

              <p
                style={{
                  fontSize: "0.9rem",
                  fontWeight: "bold",
                  marginBottom: "0.8rem",
                }}
              >
                Custos Médios por Requisição:
              </p>
              <p style={{ fontSize: "0.85rem", marginBottom: "0.5rem" }}>
                <strong>LLM:</strong>{" "}
                <span
                  style={{
                    fontFamily: "monospace",
                    fontWeight: "bold",
                    color: "#0d47a1",
                    fontSize: "0.9rem",
                  }}
                >
                  $
                  {allLLMEventsPerPeriod.reduce(
                    (sum, item) => sum + (item.total_requests || 0),
                    0,
                  ) === 0
                    ? "0.000000"
                    : (
                        allLLMEventsPerPeriod.reduce(
                          (sum, item) => sum + (item.total || 0),
                          0,
                        ) /
                        allLLMEventsPerPeriod.reduce(
                          (sum, item) => sum + (item.total_requests || 0),
                          0,
                        )
                      ).toFixed(6)}
                </span>
              </p>
              <p style={{ fontSize: "0.85rem", marginBottom: "0.5rem" }}>
                <strong>Transcrição:</strong>{" "}
                <span
                  style={{
                    fontFamily: "monospace",
                    fontWeight: "bold",
                    color: "#0d47a1",
                    fontSize: "0.9rem",
                  }}
                >
                  $
                  {allTranscriptionEventsPerPeriod.reduce(
                    (sum, item) => sum + (item.total_requests || 0),
                    0,
                  ) === 0
                    ? "0.000000"
                    : (
                        allTranscriptionEventsPerPeriod.reduce(
                          (sum, item) => sum + (item.total || 0),
                          0,
                        ) /
                        allTranscriptionEventsPerPeriod.reduce(
                          (sum, item) => sum + (item.total_requests || 0),
                          0,
                        )
                      ).toFixed(6)}
                </span>
              </p>
              <p style={{ fontSize: "0.85rem", marginBottom: "0.5rem" }}>
                <strong>Armazenamento:</strong>{" "}
                <span
                  style={{
                    fontFamily: "monospace",
                    fontWeight: "bold",
                    color: "#0d47a1",
                    fontSize: "0.9rem",
                  }}
                >
                  $
                  {allStorageEventsPerPeriod.reduce(
                    (sum, item) => sum + (item.total_requests || 0),
                    0,
                  ) === 0
                    ? "0.000000"
                    : (
                        allStorageEventsPerPeriod.reduce(
                          (sum, item) => sum + (item.total || 0),
                          0,
                        ) /
                        allStorageEventsPerPeriod.reduce(
                          (sum, item) => sum + (item.total_requests || 0),
                          0,
                        )
                      ).toFixed(6)}
                </span>
              </p>
            </CardBody>
          </Card>
        </Row>
        <Row style={{ gap: "1rem", paddingRight: "1rem" }}>
          <Card id="llm-events-card" style={{ width: "calc(50% - 0.5rem)" }}>
            <CardBody>
              <CardTitle className="mb-1">
                <b>Análises generativas</b>
              </CardTitle>
              <div
                style={{
                  fontSize: "0.8rem",
                  color: "#666",
                  marginBottom: "0.5rem",
                }}
              >
                Total: $
                {allLLMEventsPerPeriod
                  .reduce((sum, item) => sum + (item.total || 0), 0)
                  .toFixed(2)}{" "}
                | Requisições:{" "}
                {allLLMEventsPerPeriod.reduce(
                  (sum, item) => sum + (item.total_requests || 0),
                  0,
                )}{" "}
                | Quantidade:{" "}
                {Math.round(
                  allLLMEventsPerPeriod.reduce(
                    (sum, item) => sum + (item.total_quantity || 0),
                    0,
                  ) / 3,
                ).toLocaleString()}{" "}
                (tokens)
              </div>
              <EventsBarChart
                title="LLM Events"
                data={allLLMEventsPerPeriod}
                color="#1976d2"
              />
            </CardBody>
          </Card>
          <Card
            id="transcription-events-card"
            style={{ width: "calc(50% - 0.5rem)" }}
          >
            <CardBody>
              <CardTitle className="mb-1">
                <b>Transcrições</b>
              </CardTitle>
              <div
                style={{
                  fontSize: "0.8rem",
                  color: "#666",
                }}
              >
                Total: $
                {allTranscriptionEventsPerPeriod
                  .reduce((sum, item) => sum + (item.total || 0), 0)
                  .toFixed(2)}{" "}
                | Requisições:{" "}
                {allTranscriptionEventsPerPeriod.reduce(
                  (sum, item) => sum + (item.total_requests || 0),
                  0,
                )}{" "}
                | Quantidade:{" "}
                {Math.floor(
                  allTranscriptionEventsPerPeriod.reduce(
                    (sum, item) => sum + (item.total_quantity || 0),
                    0,
                  ),
                ).toLocaleString()}{" "}
                (Tempo de processamento em minutos)
              </div>
              <EventsBarChart
                title="Transcription Events"
                data={allTranscriptionEventsPerPeriod}
                color="#388e3c"
              />
            </CardBody>
          </Card>
          <Card
            id="storage-events-card"
            style={{ width: "calc(50% - 0.5rem)" }}
          >
            <CardBody>
              <CardTitle className="mb-1">
                <b>Armazenamento</b>
              </CardTitle>
              <div
                style={{
                  fontSize: "0.8rem",
                  color: "#666",
                }}
              >
                Total: $
                {allStorageEventsPerPeriod
                  .reduce((sum, item) => sum + (item.total || 0), 0)
                  .toFixed(2)}{" "}
                | Requisições:{" "}
                {allStorageEventsPerPeriod.reduce(
                  (sum, item) => sum + (item.total_requests || 0),
                  0,
                )}{" "}
                | Quantidade:{" "}
                {allStorageEventsPerPeriod
                  .reduce((sum, item) => sum + (item.total_quantity || 0), 0)
                  .toLocaleString()}{" "}
                (mb)
              </div>
              <EventsBarChart
                title="Storage Events"
                data={allStorageEventsPerPeriod}
                color="#fbc02d"
              />
            </CardBody>
          </Card>
        </Row>
      </Container>
    </>
  );
};

export default ClientSummary;
